import { decoratorsJson, groupsJson, operatorsJson } from "@corca/data";
import { latexExportsJson } from "@corca/data/latex-exports";

import type { DecoratorTypeValue, NodeJson } from "@corca/data";

const ATTACHMENT_TYPES = new Set(["label", "decorator"]);
const MENCLOSE_NOTATIONS: Partial<Record<DecoratorTypeValue, string>> = {
  boxed: "box",
  cancel: "updiagonalstrike",
  bcancel: "downdiagonalstrike",
  xcancel: "updiagonalstrike downdiagonalstrike",
};
const FUNCTION_APPLICATION = "\u2061";
const INVISIBLE_TIMES = "\u2062";
/**
 * SRE names a unit from its symbol alone, so these would read as another unit (a coulomb as
 * Celsius) or as a bare symbol; they stay plain text.
 */
const UNITS_READ_AS_TEXT = new Set(["C", "F", "T", "%", "‰", "‱", "℧", "Å"]);

type ScriptedFragment = {
  base: string;
  sub?: string;
  sup?: string;
  preSub?: string;
  preSup?: string;
  supRole?: "superscript" | "derivative" | "double-derivative";
};

type ScriptSlot = "sub" | "sup" | "preSub" | "preSup";

function escapeXml(value: unknown): string {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function token(kind: "mi" | "mn" | "mo" | "mtext", value: unknown): string {
  return `<${kind}>${escapeXml(value)}</${kind}>`;
}

function serializeFragment(fragment: ScriptedFragment): string {
  const { sub, sup, preSub, preSup, supRole } = fragment;
  if (!sub && !sup && !preSub && !preSup) return fragment.base;

  // A script element takes a fixed number of children, so a composite base has to become one.
  const base = asSingleRoot(fragment.base);
  const role = supRole ? ` data-corca-role="${supRole}"` : "";

  if (preSub || preSup) {
    const post = sub || sup ? `${sub ?? "<none/>"}${sup ?? "<none/>"}` : "";
    const pre = `${preSub ?? "<none/>"}${preSup ?? "<none/>"}`;
    return `<mmultiscripts${role}>${base}${post}<mprescripts/>${pre}</mmultiscripts>`;
  }

  if (sub && sup) {
    return `<msubsup${role}>${base}${sub}${sup}</msubsup>`;
  }
  if (sup) {
    return `<msup${role}>${base}${sup}</msup>`;
  }
  return `<msub>${base}${sub}</msub>`;
}

function addScript(
  fragment: ScriptedFragment,
  slot: ScriptSlot,
  value: string,
  supRole?: ScriptedFragment["supRole"],
): ScriptedFragment {
  if (fragment[slot]) {
    return { base: serializeFragment(fragment), [slot]: value, ...(supRole && { supRole }) };
  }
  return { ...fragment, [slot]: value, ...(supRole && { supRole }) };
}

function meaningfulChildren(node: NodeJson): NodeJson[] {
  return node.children.filter((child) => !ATTACHMENT_TYPES.has(child.type));
}

function hasSingleRootElement(value: string): boolean {
  let depth = 0;
  let roots = 0;

  for (const match of value.matchAll(/<(\/)?[a-z]+(?:\s[^>]*)?>/g)) {
    if (match[1]) {
      depth -= 1;
    } else {
      if (depth === 0) roots += 1;
      depth += 1;
    }
  }

  return roots === 1 && depth === 0;
}

function asSingleRoot(value: string): string {
  return hasSingleRootElement(value) ? value : `<mrow>${value}</mrow>`;
}

function asOperand(node: NodeJson): string {
  return asSingleRoot(nodeToMml(node));
}

function scriptForOperator(node: NodeJson) {
  if (node.type !== "operator") return;

  const operand = node.children.find((child) => child.type === "inline");
  if (!operand) return;

  switch (node.props.operatorType) {
    case "power":
      return { slot: "sup" as const, value: asOperand(operand) };
    case "superscript":
      return { slot: "sup" as const, value: asOperand(operand), role: "superscript" as const };
    case "subscript":
      return { slot: "sub" as const, value: asOperand(operand) };
    default:
      return;
  }
}

function renderInline(
  node: NodeJson,
  renderChild: (child: NodeJson) => string = nodeToMml,
): string {
  const children = meaningfulChildren(node);
  const parts: { fragment: ScriptedFragment; isOperand: boolean }[] = [];

  for (let index = 0; index < children.length; index += 1) {
    const child = children[index];
    const script = scriptForOperator(child);
    const previous = parts.at(-1);
    if (script && previous) {
      previous.fragment = addScript(previous.fragment, script.slot, script.value, script.role);
      continue;
    }
    // A function needs its next sibling to decide whether `auto` parentheses can be dropped.
    parts.push({
      fragment: {
        base:
          child.type === "function"
            ? renderFunction(child, children[index + 1])
            : renderChild(child),
      },
      isOperand: child.type !== "operatorSymbol" && child.type !== "plainText",
    });
  }

  const rendered = parts
    .map(({ fragment, isOperand }) => ({ mml: serializeFragment(fragment), isOperand }))
    .filter(({ mml }) => mml);
  // Neighbouring operands multiply, as they do when the expression is evaluated.
  return rendered
    .map(({ mml, isOperand }, index) =>
      isOperand && rendered[index - 1]?.isOperand ? `${token("mo", INVISIBLE_TIMES)}${mml}` : mml,
    )
    .join("");
}

function decoratorDefinition(node: NodeJson) {
  if (node.type !== "decorator") return;
  const key = node.props.key ?? decoratorsJson[node.props.name]?.key;
  return key ? latexExportsJson[key]?.mml : undefined;
}

function applyDecorators(base: string, node: NodeJson): string {
  return node.children
    .filter((child) => child.type === "decorator")
    .reduce((result, decorator) => {
      if (decorator.type !== "decorator") return result;

      const notation = MENCLOSE_NOTATIONS[decorator.props.name];
      if (notation) {
        return `<menclose notation="${notation}">${result}</menclose>`;
      }

      const definition = decoratorDefinition(decorator);
      const symbol = typeof definition === "string" ? definition : definition?.symbol;
      if (!symbol) return result;

      const position = typeof definition === "object" && definition.position === "under";
      const stretchy =
        typeof definition === "object" && definition.isStretchy ? ' stretchy="true"' : "";
      const accent = `<mo${stretchy}>${escapeXml(symbol)}</mo>`;
      return position ? `<munder>${result}${accent}</munder>` : `<mover>${result}${accent}</mover>`;
    }, base);
}

function renderSymbol(node: NodeJson, kind: "mi" | "mo" = "mi"): string {
  if (node.type !== "symbol") return "";

  let fragment: ScriptedFragment = {
    base: applyDecorators(token(kind, node.props.value), node),
  };

  for (const label of node.children.filter((child) => child.type === "label")) {
    if (label.type !== "label") continue;
    const value = label.children[0] ? asOperand(label.children[0]) : "<mrow></mrow>";
    if (label.props.position === "right-top") {
      fragment = addScript(fragment, "sup", value, "superscript");
    } else if (label.props.position === "right-bottom") {
      fragment = addScript(fragment, "sub", value);
    } else if (label.props.position === "left-top") {
      fragment = addScript(fragment, "preSup", value);
    } else {
      fragment = addScript(fragment, "preSub", value);
    }
  }

  return serializeFragment(fragment);
}

/**
 * Marks each name in a unit as a unit, so speech can name it: `5 kg` reads as "5 kilograms", and
 * a compound such as `kg·m/s²` keeps its factors apart.
 */
function renderUnitPart(node: NodeJson): string {
  if (node.type !== "plainText") return nodeToMml(node);

  const { value } = node.props;
  // SymPy writes the separator of a compound unit, and the 1 of a bare denominator, as text.
  if (value === "·") return token("mo", value);
  if (/^\d+$/.test(value)) return token("mn", value);
  return UNITS_READ_AS_TEXT.has(value)
    ? token("mtext", value)
    : `<mi class="MathML-Unit" mathvariant="normal">${escapeXml(value)}</mi>`;
}

function renderUnit(node: NodeJson): string {
  return meaningfulChildren(node)
    .map((child) =>
      child.type === "inline" ? renderInline(child, renderUnitPart) : renderUnitPart(child),
    )
    .join("");
}

/** A unit hangs off its value as a child, so it has to be pulled in explicitly. */
function applyUnit(base: string, node: NodeJson): string {
  const unit = node.children.find((child) => child.type === "unit");
  if (!unit) return base;

  const rendered = nodeToMml(unit);
  if (!rendered) return base;

  return unit.props.placement === "prefix" ? `${rendered}${base}` : `${base}${rendered}`;
}

function applyOwnedScripts(base: string, node: NodeJson): string {
  let fragment: ScriptedFragment = { base };
  for (const child of node.children) {
    const script = scriptForOperator(child);
    if (script) {
      fragment = addScript(fragment, script.slot, script.value, script.role);
    }
  }
  return serializeFragment(fragment);
}

function fenceSymbol(symbol: string | undefined, side: "opening" | "closing"): string {
  if (!symbol) return "";

  const group = Object.values(groupsJson).find(
    (candidate) => candidate[`${side}Symbol`].symbol === symbol,
  );
  const key = group?.[`${side}Symbol`].key;
  const mml = key ? latexExportsJson[key]?.mml : undefined;
  const value = typeof mml === "string" ? mml : symbol;
  const form =
    (side === "opening" && value === "]") || (side === "closing" && value === "[")
      ? ` form="${side === "opening" ? "prefix" : "postfix"}"`
      : "";
  return `<mo${form}>${escapeXml(value)}</mo>`;
}

/** A row of its own keeps the argument a single operand and stretches the fences to it alone. */
function fencedArgument(body: string, opening: string | undefined, closing: string | undefined) {
  return `<mrow>${fenceSymbol(opening ?? "(", "opening")}${body}${fenceSymbol(closing ?? ")", "closing")}</mrow>`;
}

function renderMatrix(node: NodeJson): string {
  if (node.type !== "matrix") return "";

  const cells = node.children.filter((child) => child.type === "inline");
  // `cols` arrives from the collaborative document, so a corrupt value must not size the padding
  // loop or the column-line list; a matrix never has more columns than it has cells.
  const cols = Math.max(1, Math.min(node.props.cols, cells.length || 1));
  const rows: string[] = [];
  for (let index = 0; index < cells.length; index += cols) {
    const row = cells.slice(index, index + cols);
    while (row.length < cols) {
      row.push({ type: "inline", props: {}, children: [] });
    }
    rows.push(`<mtr>${row.map((cell) => `<mtd>${nodeToMml(cell)}</mtd>`).join("")}</mtr>`);
  }

  const columnalign = node.props.columnalign
    ? ` columnalign="${escapeXml(node.props.columnalign)}"`
    : "";
  const augmentedBars = new Set(node.props.augmentedBarIndices ?? []);
  const columnlines = Array.from({ length: Math.max(0, cols - 1) }, (_, index) =>
    augmentedBars.has(index) ? "solid" : "none",
  );
  const columnlinesAttr = columnlines.includes("solid")
    ? ` columnlines="${columnlines.join(" ")}"`
    : "";

  return `<mtable${columnalign}${columnlinesAttr}>${rows.join("")}</mtable>`;
}

function renderGroup(node: NodeJson): string {
  if (node.type !== "group") return "";
  const matrix = node.children.find((child) => child.type === "matrix");
  if (!matrix || matrix.type !== "matrix") return "";

  const isSingleCell = matrix.props.rows === 1 && matrix.props.cols === 1;
  const body = isSingleCell ? nodeToMml(matrix.children[0]) : renderMatrix(matrix);
  const opening = node.props.casesSystemEnabled
    ? token("mo", "{")
    : fenceSymbol(node.props.openingSymbol, "opening");
  const closing = node.props.casesSystemEnabled
    ? ""
    : fenceSymbol(node.props.closingSymbol, "closing");
  const grouped = node.props.omitParentheses ? body : `${opening}${body}${closing}`;
  return applyDecorators(`<mrow>${grouped}</mrow>`, node);
}

function renderOperator(node: NodeJson): string {
  if (node.type !== "operator") return "";
  const parameters = node.children.filter((child) => child.type === "inline");
  const [first, second] = parameters;

  switch (node.props.operatorType) {
    case "fraction":
      return `<mfrac>${first ? asOperand(first) : "<mrow></mrow>"}${second ? asOperand(second) : "<mrow></mrow>"}</mfrac>`;
    case "root":
      return first && meaningfulChildren(first).length > 0
        ? `<mroot>${second ? asOperand(second) : "<mrow></mrow>"}${asOperand(first)}</mroot>`
        : `<msqrt>${second ? nodeToMml(second) : ""}</msqrt>`;
    case "derivative":
    case "double-derivative": {
      const role = node.props.operatorType;
      const mark = role === "derivative" ? "′" : "″";
      return `<msup data-corca-role="${role}">${first ? asOperand(first) : "<mrow></mrow>"}${token("mo", mark)}</msup>`;
    }
    case "factorial":
    case "dagger": {
      const symbol = node.props.name ?? operatorsJson[node.props.operatorType].symbol;
      return `<mrow>${first ? asOperand(first) : ""}${token("mo", symbol)}</mrow>`;
    }
    case "power":
    case "subscript":
    case "superscript":
      return first ? asOperand(first) : "";
    default:
      return "";
  }
}

/** Mirrors `FunctionNodeModel.shouldOmitParentheses`. */
function argumentAllowsOmission(parameters: NodeJson[]): boolean {
  if (parameters.length > 1) return false;

  const nodes = parameters[0] ? meaningfulChildren(parameters[0]) : [];
  if (nodes.length === 0) return true;
  if (nodes.length === 1) {
    const [only] = nodes;
    if (only.type !== "group") return true;

    // A group keeps its symbols in props while hiding them, and the renderer reads the getters.
    const { openingSymbol, closingSymbol, omitParentheses, casesSystemEnabled } = only.props;
    return !!openingSymbol && !!closingSymbol && !omitParentheses && !casesSystemEnabled;
  }

  return !nodes.some((child) => child.type === "operatorSymbol" || child.type === "group");
}

function renderFunction(node: NodeJson, nextSibling?: NodeJson): string {
  if (node.type !== "function") return "";
  const symbolNode = node.children.find((child) => child.type === "symbol");
  if (!symbolNode || symbolNode.type !== "symbol") return "";

  const kind = /^[\p{L}\p{N}]+$/u.test(symbolNode.props.value) ? "mi" : "mo";
  const symbol = applyOwnedScripts(renderSymbol(symbolNode, kind), node);
  const functionApplication =
    kind === "mi" && symbolNode.props.value.length > 1 ? token("mo", FUNCTION_APPLICATION) : "";
  const parameters = node.children.filter((child) => child.type === "inline");
  if (parameters.length === 0) return symbol;

  const body = parameters.map(nodeToMml).join(token("mo", ","));
  // Matches FunctionNodeModel, which shows parentheses unless told otherwise.
  const { parenthesesVisibility = "visible" } = node.props;
  const nextSiblingAllowsOmission = !nextSibling || nextSibling.type === "operatorSymbol";
  const omitParentheses =
    parenthesesVisibility === "hidden" ||
    (parenthesesVisibility === "auto" &&
      argumentAllowsOmission(parameters) &&
      nextSiblingAllowsOmission);

  const argument = omitParentheses
    ? `<mrow>${body}</mrow>`
    : fencedArgument(body, node.props.openingSymbol, node.props.closingSymbol);

  // A postfix function draws its symbol after the argument, e.g. an evaluation bar.
  return node.props.placement === "postfix"
    ? `${argument}${symbol}`
    : `${symbol}${functionApplication}${argument}`;
}

/** Serialize a Corca expression node directly to a compact presentation-MathML fragment. */
export function nodeToMml(node: NodeJson): string {
  switch (node.type) {
    case "wrapper":
    case "line":
    case "text":
    case "computedResult":
      return meaningfulChildren(node).map(nodeToMml).join("");
    case "computedReference": {
      const value = meaningfulChildren(node).map(nodeToMml).join("");
      return node.props.bracketed
        ? `<mrow>${fenceSymbol("(", "opening")}${value}${fenceSymbol(")", "closing")}</mrow>`
        : value;
    }
    case "inline":
      return renderInline(node);
    case "unit":
      return renderUnit(node);
    case "symbol":
      return renderSymbol(node);
    case "operatorSymbol": {
      const symbol = node.children.find((child) => child.type === "symbol");
      return token(
        "mo",
        symbol?.type === "symbol"
          ? symbol.props.value
          : (node.props.inputSymbol ?? node.props.name),
      );
    }
    case "variable": {
      const symbol = node.children.find((child) => child.type === "symbol");
      if (!symbol || symbol.type !== "symbol") return "";
      const base = applyOwnedScripts(renderSymbol(symbol), node);
      const parameters = node.children.filter((child) => child.type === "inline");
      if (parameters.length === 0) return applyUnit(base, node);
      const body = parameters.map(nodeToMml).join(token("mo", ","));
      // A variable with parameters is always a call such as `f(x)`, never a product.
      return applyUnit(
        `${base}${token("mo", FUNCTION_APPLICATION)}${fencedArgument(body, node.props.openingSymbol, node.props.closingSymbol)}`,
        node,
      );
    }
    case "operator":
      return renderOperator(node);
    case "group":
      return renderGroup(node);
    case "function":
      return renderFunction(node);
    case "plainText":
      return token("mtext", node.props.value);
    case "number":
      return applyUnit(applyOwnedScripts(token("mn", node.props.value), node), node);
    case "constant": {
      const child = meaningfulChildren(node).find((candidate) => candidate.type !== "unit");
      return child ? applyUnit(nodeToMml(child), node) : "";
    }
    case "label":
      return node.children.map(nodeToMml).join("");
    case "matrix":
      return renderMatrix(node);
    case "placeholder":
      // An empty slot reads as nothing, but a filled one holds real content.
      return meaningfulChildren(node).map(nodeToMml).join("");
    case "decorator":
    case "graph":
    case "graphExpression":
      return "";
  }
}
